import TriggerCard from '__pages/AutomationEngine/Engine/Steps/TriggerCard';
import DraggableStepCard, { DraggableStepCardProps } from 'pages/AutomationEngine/Engine/Steps/DraggableStepCard';
import type { Field, Step } from 'pages/AutomationEngine/types';
import { type TFunction } from 'react-i18next';
import type {
  FieldUpdatedMode,
  TriggerFieldUpdatedStep,
} from 'pages/AutomationEngine/dialogs/GoalAndTriggerWizard/subsections/FieldUpdatedGoal/types';
import { FIELD_UPDATED_MODES } from 'pages/AutomationEngine/dialogs/GoalAndTriggerWizard/subsections/FieldUpdatedGoal/contants';
import { FIELD_TYPES } from 'utility/constants';

type StepWithDetails = Omit<Step, 'details'> & {
  details: TriggerFieldUpdatedStep;
};

const hasModeDescription = (mode: FieldUpdatedMode) =>
  (
    [
      FIELD_UPDATED_MODES.ANY_EXCLUDING_BLANK,
      FIELD_UPDATED_MODES.BLANK,
      FIELD_UPDATED_MODES.SPECIFIC_VALUE,
      FIELD_UPDATED_MODES.DATE_BETWEEN,
      FIELD_UPDATED_MODES.VALUES_ADDED,
      FIELD_UPDATED_MODES.VALUES_REMOVED,
      FIELD_UPDATED_MODES.CONTAINS,
      FIELD_UPDATED_MODES.DOES_NOT_CONTAIN,
    ] as FieldUpdatedMode[]
  ).includes(mode);

const getDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const getDateTime = (dateString: string, t: TFunction) => {
  return t('{{date}} at {{time}}', {
    date: getDate(dateString),
    time: new Date(dateString).toLocaleTimeString('en', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
};

const getDateBetweenDescription = (
  field: Field,
  value: string,
  t: TFunction
) => {
  if (!value) {
    return '';
  }

  if (field.fieldType === FIELD_TYPES.DateTime.type) {
    return getDateTime(value, t);
  }

  return getDate(value);
};

const getLabelWithDeleted = (value: any, t: TFunction) => {
  const label = value?.label || value?.displayName || '';
  if (value?.deleted) {
    return `${label} ${t('[Deleted]')}`;
  }
  return label;
};

const getSpecificValue = (field: Field, value: any, t: TFunction) => {
  switch (field.fieldType) {
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
    case FIELD_TYPES.Rating.type:
      return value || '';
    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return isNaN(value) ? '' : value;
    case FIELD_TYPES.Checkbox.type:
      return value ? t('Checked') : t('Not Checked');
    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.TeamSelector.type:
    case FIELD_TYPES.Relationship.type:
    case FIELD_TYPES.YesNoMaybe.type:
      return value ? getLabelWithDeleted(value, t) : '';
    default:
      return value || '';
  }
};

const getMultiValue = (field: Field, value: any[], t: TFunction) => {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }

  const options = value.map((val) => `'${getLabelWithDeleted(val, t)}'`);

  if (value.length === 1) {
    return options[0];
  }

  if (value.length <= 3) {
    const lastOption = options.pop();
    return t('{{options}} or {{lastOption}}', {
      options: options.join(', '),
      lastOption,
    });
  }

  return t('{{options}} or {{count}} more', {
    options: options.slice(0, 2).join(', '),
    count: options.length - 2,
  });
};

const getValueDescription = (
  {
    mode,
    value,
    field,
  }: {
    mode: FieldUpdatedMode | null;
    value:
      | TriggerFieldUpdatedStep['fromValue']
      | TriggerFieldUpdatedStep['toValue'];
    field: Field;
  },
  t: TFunction
) => {
  switch (mode) {
    case FIELD_UPDATED_MODES.ANY_EXCLUDING_BLANK:
      return t('Any Value');
    case FIELD_UPDATED_MODES.BLANK:
      return t('Blank');
    case FIELD_UPDATED_MODES.SPECIFIC_VALUE:
      return `'${getSpecificValue(field, value, t)}'`;
    case FIELD_UPDATED_MODES.VALUES_ADDED:
      return t('add {{values}}', {
        values: getMultiValue(field, value, t),
      });
    case FIELD_UPDATED_MODES.VALUES_REMOVED:
      return t('remove {{values}}', {
        values: getMultiValue(field, value, t),
      });
    case FIELD_UPDATED_MODES.CONTAINS:
      return t('containing {{values}}', {
        values: getMultiValue(field, value, t),
      });
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN:
      return t('not containing {{values}}', {
        values: getMultiValue(field, value, t),
      });
    case FIELD_UPDATED_MODES.DATE_BETWEEN: {
      const from = getDateBetweenDescription(field, value.dateStart, t);
      const to = getDateBetweenDescription(field, value.dateEnd, t);
      if (!from && !to) {
        return '';
      }
      if (!from) {
        return t('Before {{to}}', { to });
      }
      if (!to) {
        return t('After {{from}}', { from });
      }
      return `${from}-${to}`;
    }
    default:
      return '';
  }
};

export const content = ({ step }: { step: StepWithDetails }) =>
  step.description;

export const getDescription = ({
  step,
  t,
}: {
  step: StepWithDetails;
  t: TFunction;
}) => {
  const fromValue =
    hasModeDescription(step.details.fromMatchMode!) && step.details.field
      ? getValueDescription(
          {
            mode: step.details.fromMatchMode,
            value: step.details.fromValue,
            field: step.details.field.fieldData,
          },
          t
        )
      : null;

  const toValue =
    hasModeDescription(step.details.toMatchMode!) && step.details.field
      ? getValueDescription(
          {
            mode: step.details.toMatchMode,
            value: step.details.toValue,
            field: step.details.field?.fieldData,
          },
          t
        )
      : null;

  return t('‘{{fieldName}}’ Updated{{from}}{{to}}', {
    fieldName: getLabelWithDeleted(step.details.field, t),
    from: fromValue ? t(' from {{fromValue}}', { fromValue }) : '',
    to: toValue ? t(' to {{toValue}}', { toValue }) : '',
  });
};

export default function Card({
  step,
  ...others
}: { step: StepWithDetails } & DraggableStepCardProps) {
  return step?.type === 'goal' ? (
    <DraggableStepCard {...others}>{content({ step })}</DraggableStepCard>
  ) : (
    <TriggerCard {...others}>{content({ step })}</TriggerCard>
  );
}
